import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  InputSizes,
  IOption,
  PageHeader,
  PaginationControl,
  Search,
  Head,
  PerformerAppPath,
  PerformerAppSmartBanner,
  RouterProps,
  isSmallDeviceWidth,
} from "component-library";
import {
  JobsFeedUsageEvents,
  LaunchDarklyClientWithFlags,
  sendJobsfeedUsageHeapEventWithUserPrefix,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { dataTestIds } from "data-testids";
import { withTranslation, WithTranslation } from "i18n";
import { facetGroupCode } from "shared/config/common";
import scrollManager from "shared/services/scrollManager";

import { MapDispatchToProps } from "shared/types/utilities";
import {
  ALL_OPPORTUNITIES_FULL_PATH,
  PAGINATION_PAGE_RANGE_DESKTOP,
  PAGINATION_PAGE_RANGE_MOBILE,
  PageTitleKey,
} from "config/common";

import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";

import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";
import { IJobFilterParams } from "models/api";

import { IJobItem, IPaginatedJobItemList } from "models/job";
import { getPageTitle } from "helpers/getPageTitle";

import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";
import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";

import {
  FilterType,
  IJobSender,
  OpenClosedRoles,
  ShowType,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";
import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";

import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";
import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";

import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";
import { IJobsPageInjectedProps } from "pages/JobsPage/JobsPage";
import {
  getOpenClosedRolesFilter,
  getOpenClosedRolesFilterOptions,
  getShowTypeFilterOptions,
  INPUT_NAMES,
} from "pages/JobsPage/JobsPage.helpers";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import {
  DEFAULT_JOBS_FEEED,
  ALL_OPPORTUNITIES_SEARCH,
  persistSearchAndResetJobsFeedStorage,
  searchRefClear,
  initSearch,
  getInitialValue,
} from "shared/helpers/common";
import RefreshButton from "components/RefreshButton/RefreshButton";
import SelectAllControl from "components/SelectAllControl/SelectAllControl";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";
import { checkIsAgent } from "shared-auth";

export interface IAllOpportunitiesPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IAllOpportunitiesPageStoreProps {
  texts: IAllOpportunitiesPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
  jobsFeed: IJobsFeedState;
}

export interface IAllOpportunitiesPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setJobsFeedData: SetJobsFeedData;
}

export interface IAllOpportunitiesPageProps
  extends
    IAllOpportunitiesPageStoreProps,
    IJobsPageInjectedProps,
    RouterProps,
    IAllOpportunitiesPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface IAllOpportunitiesPageState {
  selectedItems: JobTableItemUuid[];
}

export class AllOpportunitiesPage extends React.Component<
  IAllOpportunitiesPageProps,
  IAllOpportunitiesPageState
> {
  constructor(props: any) {
    super(props);

    this.setJobsFeedStorage(
      this.props.router.location.pathname,
      DEFAULT_JOBS_FEEED.PAGENUMBER,
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      this.props.jobsFeed.isBackToJobsFeed
    );

    const pageNumber = this.props.jobsFeed.pageNumber;

    if (this.props.jobsFeed.isBackToJobsFeed && pageNumber) {
      const [, setSearchParams] = this.props.router.searchParams;

      setSearchParams({ page: pageNumber });
    }
  }

  public static pageSize: number = 50;
  public readonly state: Readonly<IAllOpportunitiesPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const isAgent = checkIsAgent();
    const {
      filterValues,
      paginatedItems,
      isLoading,
      texts,
      renderJobsFilterControl,
      isPerformer,
      t,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.MyJobs} />
        <PageHeader
          pageTitle={
            isAgent
              ? t("common:jobs.page.projects.title")
              : t("common:jobs.page.otherOpportunities.title")
          }
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText="" />
          }
        />
        {renderJobsFilterControl(this.renderPagination())}
        <div className="g-content-area mt-1">
          <div className="g-col-lg-12">
            <div className="flex flex-col-reverse flex-wrap items-start justify-between gap-3 md:flex-row">
              <div className="flex flex-col gap-2">
                <PaginationResultStats
                  page={filterValues[FilterType.PageNumber]}
                  itemsPerPage={AllOpportunitiesPage.pageSize}
                  itemsTotal={paginatedItems.count}
                  textTemplate={texts.table.resultStats}
                />
                <SelectAllControl
                  selectItems={this.selectItems}
                  deselectItems={this.deselectItems}
                  selectedItems={selectedItems}
                  markItemsAsRead={this.markSelectedItemsAsRead}
                  markItemsAsUnread={this.markSelectedItemsAsUnread}
                  items={paginatedItems.items}
                  onSelectAllClick={() =>
                    sendJobsfeedUsageHeapEventWithUserPrefix(
                      JobsFeedUsageEvents.SelectAll
                    )
                  }
                />
              </div>
              <div className="w-full md:flex md:w-auto md:justify-end md:gap-3">
                {this.isPerformer() ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 md:contents">
                      <div className="min-w-0">
                        {this.renderOpenClosedRoles()}
                      </div>
                      <div className="min-w-0">{this.renderSorter()}</div>
                    </div>
                    <div className="mt-3 flex justify-end md:mt-0 md:contents">
                      <RefreshButton
                        onClick={this.handleRefresh}
                        className="!flex !aspect-square !h-10 !w-10 !items-center !justify-center !gap-[10px] !rounded-[10px] !border !border-grey-light-2"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 md:contents">
                      <div className="min-w-0">{this.renderFilter()}</div>
                      <div className="min-w-0">
                        {this.renderOpenClosedRoles()}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-3 md:mt-0 md:contents">
                      <div className="min-w-0">{this.renderSorter()}</div>
                      <div className="justify-self-end">
                        <RefreshButton
                          onClick={this.handleRefresh}
                          className="!flex !aspect-square !h-10 !w-10 !items-center !justify-center !gap-[10px] !rounded-[10px] !border !border-grey-light-2"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={AllOpportunitiesPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={ALL_OPPORTUNITIES_FULL_PATH}
              isPerformer={isPerformer}
              jobsFeed={this.props.jobsFeed}
              setJobsFeedData={this.props.setJobsFeedData}
              showSelectAllControl={false}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div className="g-col-lg-6">
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
            data-testid={
              dataTestIds.nuJobsApp["MySuggestions.MySuggestions.searchInput"]
            }
            initialValue={getInitialValue(filterValues)}
          />
        </div>
      </div>
    );
  };

  private renderFilter() {
    const { filterValues, t } = this.props;
    const isPerformer = this.isPerformer();
    const filterOptions = getShowTypeFilterOptions(t);

    if (isPerformer) {
      return null;
    }

    return (
      <DropdownInput<string>
        id="opportunitiesFilterType"
        name={INPUT_NAMES.showType}
        size={InputSizes.Medium}
        className="[&_.c-input-wrapper]:!mb-0"
        variant="select"
        options={filterOptions}
        selected={filterOptions.find(
          (option) => option.value === filterValues[FilterType.ShowType]
        )}
        value={filterValues[FilterType.ShowType]}
        optionSelected={this.handleShowTypeChange}
        notTruncateOptionsOnSmall={!isPerformer}
      />
    );
  }

  private renderSorter() {
    const { commonTexts, filterValues } = this.props;
    const isPerformer = this.isPerformer();

    return (
      <DropdownInput<string>
        id="opportunitiesSortingType"
        name={INPUT_NAMES.sortingType}
        size={InputSizes.Medium}
        className="[&_.c-input-wrapper]:!mb-0"
        variant="select"
        options={commonTexts.sortingOptions}
        selected={commonTexts.sortingOptions.find(
          (option) => option.value === filterValues[FilterType.SortingType]
        )}
        value={filterValues[FilterType.SortingType]}
        optionSelected={this.handleSortingTypeChange}
        notTruncateOptionsOnSmall={!isPerformer}
      />
    );
  }

  private renderOpenClosedRoles() {
    const openClosedRoleOptions = getOpenClosedRolesFilterOptions(this.props.t);

    return getOpenClosedRolesFilter(
      this.props,
      (option: IOption<OpenClosedRoles>) => {
        sendJobsfeedUsageHeapEventWithUserPrefix(
          JobsFeedUsageEvents.FilterShowOpenClose
        );
        this.props.changeFilters({
          [FilterType.OpenClosedRoles]: option.value,
        });
      },
      openClosedRoleOptions,
      InputSizes.Medium,
      "[&_.c-input-wrapper]:!mb-0"
    );
  }

  private renderPagination() {
    const { paginatedItems, filterValues } = this.props;
    const pageNumber = filterValues[FilterType.PageNumber];
    const pageCount = Math.ceil(
      paginatedItems.count / AllOpportunitiesPage.pageSize
    );

    return (
      <div className="ml-auto">
        <PaginationControl
          handlePageChange={this.handleUpdatePageNumber}
          pageRangeDisplayed={
            isSmallDeviceWidth()
              ? PAGINATION_PAGE_RANGE_MOBILE
              : PAGINATION_PAGE_RANGE_DESKTOP
          }
          pageCount={pageCount}
          selectedPage={pageNumber}
          initialSelectedPage={pageNumber}
          arePageBreaksMandatory
        />
      </div>
    );
  }

  private readonly setJobsFeedStorage = (
    pathname: string,
    pageNumber: string,
    breakdownId: string,
    isBackToJobsFeed: boolean
  ) => {
    this.props.setJobsFeedData({
      pathname,
      pageNumber,
      breakdownId,
      isBackToJobsFeed,
    });
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    this.setJobsFeedStorage(
      this.props.jobsFeed.pathname,
      String(page),
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );

    setSearchParams({ page: String(page) });
  };

  private handleShowTypeChange = (option: IOption<ShowType>) => {
    sendJobsfeedUsageHeapEventWithUserPrefix(
      JobsFeedUsageEvents.FilterShowRoleType
    );
    this.props.changeFilters({ [FilterType.ShowType]: option.value });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.FilterSort);
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    persistSearchAndResetJobsFeedStorage({
      setJobsFeedStorage: this.setJobsFeedStorage,
      pathName: this.props.router.location.pathname,
      tab: ALL_OPPORTUNITIES_SEARCH,
      search,
    });

    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    searchRefClear(this.searchRef);
    this.handleSearch(undefined);
  };

  private isPerformer() {
    return this.props.userType === AccountType.Performer;
  }

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.MarkAsRead);
  };

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.MarkAsUnread);
  };

  private markSelectedItemsAsRead = (itemIds: JobTableItemUuid[]) => {
    void this.handleMarkAsRead(itemIds);
  };

  private markSelectedItemsAsUnread = (itemIds: JobTableItemUuid[]) => {
    void this.handleMarkAsUnread(itemIds);
  };

  private handleRefresh = () => {
    this.props.changeFilters({
      [FilterType.PageNumber]: this.props.filterValues[FilterType.PageNumber],
    });
    this.props.refreshCounters();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.Refresh);
  };

  private initSearch() {
    initSearch(
      ALL_OPPORTUNITIES_SEARCH,
      this.props.jobsFeed.pageNumber,
      this.props.changeFilters
    );
  }

  private registerPageView = () => {
    const { pageTitle } = this.props.texts;

    if (this.isPerformer()) {
      return GTMEvents.jobsFeed.pageViews.allOpportunities(pageTitle);
    }

    return GTMEvents.jobsFeed.pageViews.projects(pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IAllOpportunitiesPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        ownProps.userType === AccountType.Performer
          ? PageTitleKey.JobsFeedAllOpportunities
          : PageTitleKey.JobsFeedProjects
      ),
    },
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
    isPerformer: userType === AccountType.Performer,
    jobsFeed: state.jobsFeed,
  };
};

const mapDispatchToProps: MapDispatchToProps<IAllOpportunitiesPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
  setJobsFeedData,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.All,
  filterContractTypes(facetGroups, userType) {
    if (userType === AccountType.Agent) {
      return facetGroups;
    }

    return facetGroups.filter(
      (facetGroup) => facetGroup.code === facetGroupCode.contractType.lowPay
    );
  },
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IAllOpportunitiesPageProps>(withJobsFilterOptions),
  withTranslation()
)(AllOpportunitiesPage);
